// Sales, as a board reads it (`GET /api/reports/sales`).
//
// One screen: six figures for the period, then the business month by month
// split by partner, and the partners side by side. The figures cover the
// period picked; the chart and the scorecard always cover a year up to the
// period's end, because a board reads a trend over a year.

import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useCallback, useMemo, useState, type ReactNode } from "react";
import type { Currency, SalesReport } from "@crm/shared";
import styles from "./SalesPage.module.css";
import {
  amountOf,
  bucketFor,
  changeText,
  periodEnd,
  periodShortcuts,
  type Measure,
} from "./salesModel.js";
import {
  BOARD_METRICS,
  METRIC_LABEL,
  boardColumns,
  monthLabel,
  boardFigures,
  monthlyTotals,
  partnerSlots,
  scorecard,
  topDeals,
  trendWindow,
  winRecord,
  dealSlots,
  type BoardSplit,
  type PartnerSlots,
} from "./salesBoard.js";
import { PartnerColumns, PartnerLegend, Sparkline } from "./boardCharts.js";
import { PeriodPicker } from "../../components/PeriodPicker.js";
import {
  salesStateToSearch,
  searchToSalesState,
  type SalesSearch,
  type SalesState,
  type SideView,
} from "./salesSearch.js";
import { QueryBoundary } from "../../design/QueryBoundary.js";
import { Segmented, type SegmentedOption } from "../../design/Segmented.js";
import { usePageChrome } from "../../design/pageChrome.js";
import { todayIsoDate } from "../../format/calendarDate.js";
import { dealStateLabel } from "../../format/labels.js";
import { formatCompactMoney } from "../../format/money.js";
import { useReportCurrency } from "../../format/reportCurrency.js";
import { useSalesReport } from "../../api/queries.js";

const WEIGHTING: ReadonlyArray<SegmentedOption<"yes" | "no">> = [
  { value: "yes", label: "Weighted" },
  { value: "no", label: "Full value" },
];

const BASES: ReadonlyArray<SegmentedOption<"gross" | "our_share">> = [
  { value: "gross", label: "Gross" },
  { value: "our_share", label: "Our share" },
];

const SPLITS: ReadonlyArray<SegmentedOption<BoardSplit>> = [
  { value: "partner", label: "By partner" },
  { value: "deal", label: "By deal" },
];

const SIDE_TABS: ReadonlyArray<{ value: SideView; label: string }> = [
  { value: "partners", label: "Partners" },
  { value: "deals", label: "Top deals" },
];

/** An open deal nobody has touched this long reads as cold in the deal list. */
const STALE_DAYS = 30;

export function SalesPage(): ReactNode {
  const search = useSearch({ from: "/sales" });
  const navigate = useNavigate();
  const currency = useReportCurrency();
  const today = todayIsoDate();
  const state = useMemo(() => searchToSalesState(search, today), [search, today]);

  const setState = useCallback(
    (next: SalesState) => {
      void navigate({ to: "/sales", search: salesStateToSearch(next, today) as SalesSearch });
    },
    [navigate, today],
  );

  const end = periodEnd(state.period, today);
  const trend = trendWindow(state.period.from, end);
  const period = useSalesReport({
    from: state.period.from,
    to: state.period.to,
    bucket: bucketFor(state.period, today),
    partnerId: null,
    scope: "rolled_up",
    currency,
    coldDays: STALE_DAYS,
  });
  const year = useSalesReport({
    from: trend.from,
    to: state.period.to,
    bucket: "month",
    partnerId: null,
    scope: "rolled_up",
    currency,
    coldDays: STALE_DAYS,
  });

  usePageChrome({ title: "Sales performance" });

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <PeriodPicker
          period={state.period}
          today={today}
          shortcuts={periodShortcuts(today)}
          onChange={(next) => setState({ ...state, period: next })}
        />
        <div className={styles.spacer} />
        <Segmented
          label="Weighting"
          variant="inset"
          options={WEIGHTING}
          value={state.measure.weighted ? "yes" : "no"}
          onChange={(value) =>
            setState({ ...state, measure: { ...state.measure, weighted: value === "yes" } })
          }
        />
        <Segmented
          label="Value"
          variant="inset"
          options={BASES}
          value={state.measure.basis}
          onChange={(basis) => setState({ ...state, measure: { ...state.measure, basis } })}
        />
      </div>

      <QueryBoundary query={period} label="sales">
        {(periodReport) => (
          <QueryBoundary query={year} label="sales trend">
            {(yearReport) => (
              <Board period={periodReport} year={yearReport} state={state} onChange={setState} />
            )}
          </QueryBoundary>
        )}
      </QueryBoundary>
    </div>
  );
}

function Board({
  period,
  year,
  state,
  onChange,
}: {
  period: SalesReport;
  year: SalesReport;
  state: SalesState;
  onChange: (next: SalesState) => void;
}): ReactNode {
  const measure = state.measure;
  const money = (minor: number) => formatCompactMoney(minor, year.currency);
  const partners = useMemo(() => partnerSlots(year, measure), [year, measure]);
  const byDeal = state.split === "deal";
  const slots = useMemo(
    () => (byDeal ? dealSlots(year, state.chart, measure) : partners),
    [byDeal, year, state.chart, measure, partners],
  );
  // A row of the scorecard under the pointer picks its partner out for as long as it is there.
  const [hoverPartner, setHoverPartner] = useState<string | null>(null);
  const picked =
    state.focus !== null && slots.slots.some((slot) => slot.id === state.focus)
      ? state.focus
      : null;
  const focus = hoverPartner === null || byDeal ? picked : partners.slotOf(hoverPartner).id;
  const months = year.buckets.map((bucket) => bucket.start.slice(0, 7));
  const yearSpan = monthSpan(months[0], months[months.length - 1]);
  const ahead = year.forecast_months.map((month) => month.month);

  return (
    <>
      <Figures period={period} year={year} measure={measure} money={money} />
      <div className={styles.workspace}>
        <section className={styles.panel} aria-label="By partner">
          <Tabs
            label="Chart"
            tabs={BOARD_METRICS.map((metric) => ({ value: metric, label: METRIC_LABEL[metric] }))}
            value={state.chart}
            onChange={(chart) => onChange({ ...state, chart })}
            span={
              state.chart === "forecast" ? monthSpan(ahead[0], ahead[ahead.length - 1]) : yearSpan
            }
          >
            <Segmented
              label="Split"
              variant="inset"
              options={SPLITS}
              value={state.split}
              onChange={(split) => onChange({ ...state, split, focus: null })}
            />
          </Tabs>
          <PartnerLegend
            slots={slots.slots}
            focus={focus}
            onFocus={(next) => onChange({ ...state, focus: next })}
          />
          <div className={styles.chartBody}>
            <PartnerColumns
              columns={boardColumns(year, state.chart, measure, slots, state.split)}
              focus={focus}
              money={money}
              label={`${METRIC_LABEL[state.chart]} by ${state.split}`}
            />
          </div>
        </section>
        <section className={styles.panel} aria-label="Partners and deals">
          <Tabs
            label="List"
            tabs={SIDE_TABS}
            value={state.side}
            onChange={(side) => onChange({ ...state, side })}
            span={state.side === "partners" ? yearSpan : undefined}
          />
          <div className={styles.panelBody}>
            {state.side === "partners" ? (
              <Scorecard
                report={year}
                measure={measure}
                slots={partners}
                money={money}
                onHover={setHoverPartner}
              />
            ) : (
              <TopDeals
                report={period}
                measure={measure}
                slots={partners}
                focus={byDeal ? null : picked}
                money={money}
              />
            )}
          </div>
        </section>
      </div>
    </>
  );
}

function Tabs<T extends string>({
  label,
  tabs,
  value,
  onChange,
  span,
  children,
}: {
  label: string;
  tabs: ReadonlyArray<{ value: T; label: string }>;
  value: T;
  onChange: (value: T) => void;
  /** The months the panel covers, at the right of its tabs. */
  span?: string;
  /** A control at the right, after the span. */
  children?: ReactNode;
}): ReactNode {
  return (
    <div className={styles.tabs}>
      <div className={styles.tabList} role="tablist" aria-label={label}>
        {tabs.map((tab) => (
          <button
            key={tab.value}
            type="button"
            role="tab"
            aria-selected={tab.value === value}
            className={`${styles.tab} ${tab.value === value ? styles.tabActive : ""}`}
            onClick={() => onChange(tab.value)}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {span !== undefined && <span className={styles.span}>{span}</span>}
      {children !== undefined && <div className={styles.tabControl}>{children}</div>}
    </div>
  );
}

/** "Nov 2025 – Oct 2026" */
function monthSpan(first: string | undefined, last: string | undefined): string | undefined {
  if (first === undefined || last === undefined) return undefined;
  return first === last ? monthLabel(first) : `${monthLabel(first)} – ${monthLabel(last)}`;
}

// ----------------------------------------------------------------- figures

function Figures({
  period,
  year,
  measure,
  money,
}: {
  period: SalesReport;
  year: SalesReport;
  measure: Measure;
  money: (minor: number) => string;
}): ReactNode {
  const f = boardFigures(period, measure);
  const totals = year.partners.reduce(
    (sum, p) => ({ won: sum.won + p.won_count, lost: sum.lost + p.lost_count }),
    { won: 0, lost: 0 },
  );
  const record = winRecord(totals.won, totals.lost);
  const delta = (now: number, start: number | null) => {
    if (start === null) return null;
    const diff = now - start;
    if (diff === 0) return <span className={styles.flat}>no change</span>;
    const pct = changeText(now, start);
    return (
      <span className={diff > 0 ? styles.up : styles.down}>
        {diff > 0 ? "+" : "−"}
        {money(Math.abs(diff))}
        {pct === null ? "" : ` (${pct})`}
      </span>
    );
  };
  return (
    <div className={styles.figures}>
      <Figure
        label="Signed ARR"
        value={money(f.arr.value)}
        sub={<>{delta(f.arr.value, f.arr.start)}</>}
        spark={monthlyTotals(year, "arr", measure)}
      />
      <Figure
        label="Pipeline"
        value={money(f.pipeline.value)}
        sub={<>{delta(f.pipeline.value, f.pipeline.start)}</>}
        spark={monthlyTotals(year, "pipeline", measure)}
      />
      <Figure
        label="New pipeline"
        value={money(f.added.value)}
        sub={<span>{plural(f.added.count, "deal")}</span>}
        spark={monthlyTotals(year, "new", measure)}
      />
      <Figure
        label="Bookings"
        value={money(f.bookings.value)}
        sub={<span>{plural(f.bookings.count, "deal")} won</span>}
        spark={monthlyTotals(year, "bookings", measure)}
      />
      <Figure
        label="Win rate"
        value={record.rate === null ? "—" : `${Math.round(record.rate * 100)}%`}
        sub={<span>{`${record.won} won · ${record.lost} lost · 12 mo`}</span>}
      />
      <Figure
        label="Sales cycle"
        value={year.median_cycle_days === null ? "—" : `${Math.round(year.median_cycle_days)} days`}
        sub={<span>median · 12 mo</span>}
      />
    </div>
  );
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

function Figure({
  label,
  value,
  sub,
  spark,
}: {
  label: string;
  value: string;
  sub: ReactNode;
  spark?: readonly number[];
}): ReactNode {
  return (
    <div className={styles.figure} role="group" aria-label={label}>
      <div className={styles.figureText}>
        <div className={styles.figureLabel}>{label}</div>
        <div className={styles.figureValue}>{value}</div>
        <div className={styles.figureSub}>{sub}</div>
      </div>
      {spark !== undefined && <Sparkline values={spark} />}
    </div>
  );
}

// --------------------------------------------------------------- scorecard

function Scorecard({
  report,
  measure,
  slots,
  money,
  onHover,
}: {
  report: SalesReport;
  measure: Measure;
  slots: PartnerSlots;
  money: (minor: number) => string;
  onHover: (partnerId: string | null) => void;
}): ReactNode {
  const rows = scorecard(report, measure, slots);
  const total = rows.reduce(
    (sum, row) => ({
      arr: sum.arr + row.arr,
      pipeline: sum.pipeline + row.pipeline,
      won: sum.won + row.won,
      wins: sum.wins + row.record.won,
      losses: sum.losses + row.record.lost,
    }),
    { arr: 0, pipeline: 0, won: 0, wins: 0, losses: 0 },
  );
  const record = winRecord(total.wins, total.losses);
  return (
    <table className={styles.scorecard} onMouseLeave={() => onHover(null)}>
      <thead>
        <tr>
          <th>Partner</th>
          <th>ARR</th>
          <th>Pipeline</th>
          <th>Won</th>
          <th>Win rate</th>
          <th>Cycle</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.partnerId} onMouseEnter={() => onHover(row.partnerId)}>
            <td>
              <div className={styles.partnerCell}>
                <span
                  className={styles.swatch}
                  style={{ background: row.colour }}
                  aria-hidden="true"
                />
                <Link
                  to="/partners/$partnerId"
                  params={{ partnerId: row.partnerId }}
                  className={styles.partnerName}
                >
                  {row.name}
                </Link>
              </div>
            </td>
            <td>{row.arr === 0 ? "—" : money(row.arr)}</td>
            <td>{row.pipeline === 0 ? "—" : money(row.pipeline)}</td>
            <td>{row.won === 0 ? "—" : money(row.won)}</td>
            <td>
              {row.record.rate === null ? "—" : `${Math.round(row.record.rate * 100)}%`}
              {row.record.rate !== null && (
                <span
                  className={styles.cellSub}
                >{` ${row.record.won}/${row.record.won + row.record.lost}`}</span>
              )}
            </td>
            <td>{row.cycleDays === null ? "—" : `${Math.round(row.cycleDays)}d`}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr>
          <td>Total</td>
          <td>{money(total.arr)}</td>
          <td>{money(total.pipeline)}</td>
          <td>{money(total.won)}</td>
          <td>
            {record.rate === null ? "—" : `${Math.round(record.rate * 100)}%`}
            {record.rate !== null && (
              <span className={styles.cellSub}>{` ${record.won}/${record.won + record.lost}`}</span>
            )}
          </td>
          <td>
            {report.median_cycle_days === null ? "—" : `${Math.round(report.median_cycle_days)}d`}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

function TopDeals({
  report,
  measure,
  slots,
  focus,
  money,
}: {
  report: SalesReport;
  measure: Measure;
  slots: PartnerSlots;
  /** A partner picked out in the chart lists only its own deals. */
  focus: string | null;
  money: (minor: number) => string;
}): ReactNode {
  const deals = topDeals(report, measure, 25, (deal) =>
    focus === null ? true : slots.slotOf(deal.partner_id).id === focus,
  );
  if (deals.length === 0) return <div className={styles.empty}>No open deals.</div>;
  return (
    <ol className={styles.list} aria-label="Top deals">
      {deals.map((deal) => (
        <li key={deal.deal_id} className={styles.row}>
          <span
            className={styles.swatch}
            style={{ background: slots.colourOf(deal.partner_id) }}
            aria-hidden="true"
          />
          <div className={styles.rowMain}>
            <Link to="/deals/$dealId" params={{ dealId: deal.deal_id }} className={styles.rowName}>
              {deal.customer_name}
            </Link>
            <span className={styles.rowSub}>
              {`${deal.partner_name} · ${dealStateLabel(deal.state)}`}
              {deal.idle_days >= STALE_DAYS && (
                <span className={styles.stale}>{` · ${deal.idle_days}d quiet`}</span>
              )}
            </span>
          </div>
          <span className={styles.rowValue}>{money(amountOf(deal.amounts, measure))}</span>
        </li>
      ))}
    </ol>
  );
}
